import React, { useReducer, ChangeEvent, useState, useMemo, useRef, useCallback } from 'react';
import { Box, MenuItem, TableCell, Tooltip, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { useStyles } from './styles';
import { useStyles as useSharedStyles } from '../../styles';
import {
  AgentDto,
  LlmProviderDto,
  TranscriberResponseDto,
  VoiceDto,
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useLlmModelListControllerModelListQuery,
} from '@shared/services/apiService/apiService';
import { useEffect } from 'react';
import { useCreateAgent } from '@features/Admin/Agent/hooks/useCreateAgent';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@shared/store';
import { selectors } from '@shared/store/ducks';
import { AgentProfileRequestDto } from '@features/Admin/Agent/model/types';
import { accentOptions, DEFAULT_NO_ROLE_AGENT_PROFILE, mapLanguageToISO639 } from './constants';
import { formatTranscriberLabel } from '@shared/utils/transcriberDisplay';
import { RolePicker } from './RolePicker';
import { VoicePicker } from './VoicePicker';
import { playVoicePreviewFromUserGesture } from '../playVoicePreview';
import {
  CompanySettingsLists,
  NO_REMOVED_AGENT_SETTINGS,
  RemovedAgentSettings,
  getRemovedAgentSettings,
  getUnavailableSelections,
  hasRemovedAgentSettings,
} from '@shared/utils/removedAgentSettings';

interface State {
  agent: string;
  sex: string;
  voice?: string;
  language: string;
  transcriberId: string;
  profile?: AgentProfileRequestDto;
}

type FieldKey =
  | 'agent'
  | 'sex'
  | 'voice'
  | 'language'
  | 'transcriberId'
  | 'profile'
  | 'llmProviderId'
  | 'llmModel';

const FIELD_FOCUS_ORDER: FieldKey[] = [
  'agent',
  'sex',
  'voice',
  'language',
  'transcriberId',
  'llmProviderId',
  'llmModel',
  'profile',
];

enum ActionEnum {
  SET_AGENT = 'SET_AGENT',
  SET_SEX = 'SET_SEX',
  SET_VOICE = 'SET_VOICE',
  SET_LANGUAGE = 'SET_LANGUAGE',
  SET_TRANSCRIBER = 'SET_TRANSCRIBER',
  SET_PROFILE = 'SET_PROFILE',
  RESET = 'RESET',
}

type Action =
  | { type: ActionEnum.SET_AGENT; payload: string }
  | { type: ActionEnum.SET_SEX; payload: string }
  | { type: ActionEnum.SET_VOICE; payload?: string }
  | { type: ActionEnum.SET_LANGUAGE; payload: string }
  | { type: ActionEnum.SET_TRANSCRIBER; payload: string }
  | { type: ActionEnum.SET_PROFILE; payload?: AgentProfileRequestDto }
  | { type: ActionEnum.RESET };

const initialAgentState: State = {
  agent: '',
  sex: '',
  voice: '',
  language: '',
  transcriberId: '',
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionEnum.SET_AGENT:
      return { ...state, agent: action.payload };
    case ActionEnum.SET_SEX:
      return { ...state, sex: action.payload };
    case ActionEnum.SET_VOICE:
      return { ...state, voice: action.payload };
    case ActionEnum.SET_TRANSCRIBER:
      return { ...state, transcriberId: action.payload };
    case ActionEnum.SET_LANGUAGE:
      return { ...state, language: action.payload };
    case ActionEnum.SET_PROFILE:
      return { ...state, profile: action.payload };
    case ActionEnum.RESET:
      return initialAgentState;
    default:
      return state;
  }
};

const buildEditAgentState = (
  agent: AgentDto,
  removed: RemovedAgentSettings,
  voiceData?: VoiceDto[],
): State => {
  const voice = removed.voice
    ? null
    : (voiceData?.find((v) => v.id === agent.voiceId) ?? agent.voice ?? null);

  return {
    agent: agent.name,
    sex: voice?.gender || '',
    language: mapLanguageToISO639((agent.languages?.length && agent?.languages[0]) || ''),
    transcriberId: removed.transcriber ? '' : agent.transcriberId || agent.transcriber?.id || '',
    voice: voice?.id || '',
    profile: agent.profile || DEFAULT_NO_ROLE_AGENT_PROFILE,
  };
};

const CompositeFieldContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 0 }}>
      {children}
    </Box>
  );
};

const FieldErrorSlot: React.FC<{ message?: string; className: string }> = ({
  message,
  className,
}) => (
  <Typography className={className} sx={{ visibility: message ? 'visible' : 'hidden' }}>
    {message || '\u00A0'}
  </Typography>
);

const CustomSelectField: React.FC<{
  control: any;
  labelId: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => void;
  placeholder: string;
  options: { key: string; value: string; label: string; icon?: React.ReactNode }[];
  disabled?: boolean;
  renderValue?: (selected: any) => React.ReactNode;
  className?: string;
  error?: boolean;
}> = ({
  control,
  labelId,
  name,
  value,
  onChange,
  placeholder,
  options,
  disabled,
  renderValue,
  className,
  error = false,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const defaultRenderValue = (value: string) => {
    if (!value) {
      return <Typography color={theme.palette.extendedGrey?.neutral}>{placeholder}</Typography>;
    }
    return <>{value}</>;
  };

  return (
    <CustomSelect
      control={control}
      labelId={labelId}
      name={name}
      onChange={onChange}
      id={`${labelId}-id`}
      disabled={disabled}
      value={value}
      IconComponent={IconChevronDown}
      className={className || classes.select}
      error={error}
      displayEmpty
      renderValue={renderValue ? renderValue : defaultRenderValue}>
      {options.map((option) => (
        <MenuItem key={option.key} value={option.value}>
          <Box className={classes.selectMenuItemContent}>
            {option.icon}
            {!option.icon && option.label}
          </Box>
        </MenuItem>
      ))}
    </CustomSelect>
  );
};

interface ManageAgentProps {
  onClose: () => Promise<void> | void;
  onCreated?: () => Promise<void>;
  agents: AgentDto[];
  agent: AgentDto | null;
  squadId?: string | null;
  transcriberData?: TranscriberResponseDto[];
  voiceData?: VoiceDto[];
  llmProviders?: LlmProviderDto[];
  isCompanyLoading?: boolean;
  isCompanyError?: boolean;
}

export const ManageAgent: React.FC<ManageAgentProps> = ({
  onClose,
  onCreated,
  agents,
  agent,
  squadId,
  transcriberData,
  voiceData,
  llmProviders,
  isCompanyLoading = false,
  isCompanyError = false,
}) => {
  const companyLists = useMemo<CompanySettingsLists | undefined>(
    () =>
      isCompanyLoading
        ? undefined
        : { voices: voiceData, transcribers: transcriberData, llmProviders },
    [isCompanyLoading, voiceData, transcriberData, llmProviders],
  );

  const isWaitingForCompany = Boolean(agent) && (isCompanyLoading || !llmProviders);
  const removedSettings = useMemo(
    () => (agent ? getRemovedAgentSettings(agent, companyLists) : NO_REMOVED_AGENT_SETTINGS),
    [agent, companyLists],
  );

  const [state, dispatch] = useReducer(
    reducer,
    agent ? buildEditAgentState(agent, removedSettings, voiceData) : initialAgentState,
  );

  const user = useAppSelector(selectors.auth.getUser);
  const { t } = useTranslation();
  const theme = useTheme();
  const hasNoLlmProviders = !isCompanyLoading && (llmProviders?.length ?? 0) === 0;
  const noLlmProvidersMessage = t(
    'adminMainPage.agents.fieldValidation.noLlmProvidersAvailable',
  ) as string;

  const { classes } = useStyles();
  const { classes: sharedClasses } = useSharedStyles();

  const { campaignId } = useParams();

  const {
    control,
    isLoading: isCreatingAgent,
    setValue,
    onSubmit,
  } = useCreateAgent(campaignId || '', agents, squadId);

  const { data: agentProfileTypes } = useAgentProfileListControllerAgentProfileListForUsersQuery({
    filter: 'system,custom',
  });

  const agentProfileSelectList = useMemo(
    () => agentProfileTypes?.concat([DEFAULT_NO_ROLE_AGENT_PROFILE]),
    [agentProfileTypes],
  );

  // LLM config local state
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [providerAllowsCustom, setProviderAllowsCustom] = useState<boolean>(false);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [customModelKey, setCustomModelKey] = useState<string>('');

  const isSelectedProviderInCompany = Boolean(
    selectedProviderId && llmProviders?.some((p) => p.id === selectedProviderId),
  );
  const { data: llmModels, currentData: currentLlmModels } =
    useLlmModelListControllerModelListQuery(
      { id: selectedProviderId as string },
      { skip: !isSelectedProviderInCompany || providerAllowsCustom },
    );

  const [isPlayingVoice, setIsPlayingVoice] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [saveShake, setSaveShake] = useState(false);
  const [voiceResetHint, setVoiceResetHint] = useState(false);
  // Selections cleared because a refetched list no longer has them (agent's own or replacements)
  const [staleSelections, setStaleSelections] =
    useState<RemovedAgentSettings>(NO_REMOVED_AGENT_SETTINGS);
  const fieldAnchorRefs = useRef<Partial<Record<FieldKey, HTMLTableCellElement | null>>>({});
  // MUI types TableCell's ref as Ref<unknown>; the cell is always a table cell element
  const setFieldAnchor =
    (...keys: FieldKey[]) =>
    (el: unknown) => {
      for (const key of keys) {
        fieldAnchorRefs.current[key] = el as HTMLTableCellElement | null;
      }
    };
  const [fieldErrors, setFieldErrors] = useState<Record<FieldKey, string>>({
    agent: '',
    sex: '',
    voice: '',
    language: '',
    transcriberId: '',
    profile: '',
    llmProviderId: '',
    llmModel: '',
  });

  // Initialize the edit form once per agent, after the company lists are known. A later company
  // refetch must not erase a replacement the user already picked.
  const initializedAgentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!agent || isWaitingForCompany) return;
    if (initializedAgentIdRef.current === agent.id) return;
    initializedAgentIdRef.current = agent.id;

    const next = buildEditAgentState(agent, removedSettings, voiceData);
    dispatch({ type: ActionEnum.SET_AGENT, payload: next.agent });
    dispatch({ type: ActionEnum.SET_SEX, payload: next.sex });
    dispatch({ type: ActionEnum.SET_VOICE, payload: next.voice });
    dispatch({ type: ActionEnum.SET_LANGUAGE, payload: next.language });
    dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: next.transcriberId });

    // Prefill LLM configuration
    if (removedSettings.llmProvider) {
      setSelectedProviderId('');
      setProviderAllowsCustom(false);
      setSelectedModelId('');
      setCustomModelKey('');
    } else if (llmProviders && agent.llmProviderId) {
      setSelectedProviderId(agent.llmProviderId);
      const provider = llmProviders.find((p) => p.id === agent.llmProviderId);
      const allowsCustom =
        provider?.allowsCustomModels ?? agent.llmProvider?.allowsCustomModels ?? false;
      setProviderAllowsCustom(allowsCustom);
      if (allowsCustom) {
        setCustomModelKey(agent.llmCustomModelKey || '');
        setSelectedModelId('');
      } else {
        setSelectedModelId(removedSettings.llmModel ? '' : agent.llmModelId || '');
        setCustomModelKey('');
      }
    }
  }, [agent, isWaitingForCompany, removedSettings, voiceData, llmProviders]);

  useEffect(() => {
    if (!agent) return;
    dispatch({
      type: ActionEnum.SET_PROFILE,
      payload:
        agentProfileSelectList?.find(
          (p) => (p.name as unknown as string) === (agent.profile?.name as unknown as string),
        ) || DEFAULT_NO_ROLE_AGENT_PROFILE,
    });
  }, [agent, agentProfileSelectList]);

  useEffect(() => {
    if (agent) return;
    dispatch({ type: ActionEnum.RESET });
    if (llmProviders && llmProviders.length > 0) {
      const firstProvider = llmProviders[0];
      setSelectedProviderId(firstProvider.id);
      setProviderAllowsCustom(firstProvider.allowsCustomModels);
      setSelectedModelId('');
      setCustomModelKey('');
    } else {
      setSelectedProviderId('');
      setProviderAllowsCustom(false);
      setSelectedModelId('');
      setCustomModelKey('');
    }
  }, [agent, agentProfileSelectList, llmProviders]);

  useEffect(() => {
    // Edit init effect owns provider selection for existing agents. Running fallback
    // here with a stale empty selectedProviderId can overwrite saved LLM provider.
    if (agent) return;

    if (!llmProviders?.length) {
      if (selectedProviderId) {
        setSelectedProviderId('');
        setProviderAllowsCustom(false);
        setSelectedModelId('');
        setCustomModelKey('');
      }
      return;
    }
    if (selectedProviderId && llmProviders.some((p) => p.id === selectedProviderId)) {
      return;
    }
    const fallback = llmProviders[0];
    setSelectedProviderId(fallback.id);
    setProviderAllowsCustom(fallback.allowsCustomModels);
    setSelectedModelId('');
    setCustomModelKey('');
  }, [agent, llmProviders, selectedProviderId]);

  useEffect(() => {
    if (!agent && !providerAllowsCustom && llmModels && llmModels.length > 0 && !selectedModelId) {
      setSelectedModelId(llmModels[0].id);
    }
  }, [agent, providerAllowsCustom, llmModels, selectedModelId]);

  useEffect(() => {
    if (agent) return;
    dispatch({ type: ActionEnum.SET_PROFILE, payload: DEFAULT_NO_ROLE_AGENT_PROFILE });
  }, [agent]);

  useEffect(() => {
    if (agent || state.transcriberId || !transcriberData?.length) return;
    if (transcriberData.length === 1) {
      dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: transcriberData[0].id });
    }
  }, [agent, state.transcriberId, transcriberData]);

  useEffect(() => {
    if (agent || state.language) return;
    const defaultLanguage = accentOptions.find((option) => option.key === 'eng');
    if (defaultLanguage) {
      dispatch({ type: ActionEnum.SET_LANGUAGE, payload: defaultLanguage.key });
    }
  }, [agent, state.language]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  const stopAudioPlayback = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    }
  };

  const focusFirstError = useCallback((errors: Record<FieldKey, string>) => {
    const firstKey = FIELD_FOCUS_ORDER.find((key) => errors[key]);
    if (!firstKey) return;

    const cell = fieldAnchorRefs.current[firstKey];
    cell?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    const focusable = cell?.querySelector<HTMLElement>(
      'input, button, [role="combobox"], .MuiSelect-select',
    );
    focusable?.focus({ preventScroll: true });
  }, []);

  const handleInputChange =
    (type: Action['type']) => (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      const clearFieldError = (field: FieldKey) => {
        if (!showValidation) return;
        setFieldErrors((prev) => ({ ...prev, [field]: '' }));
      };

      switch (type) {
        case ActionEnum.SET_SEX: {
          const hadVoice = Boolean(state.voice);
          dispatch({ type: ActionEnum.SET_SEX, payload: event.target.value as string });
          dispatch({ type: ActionEnum.SET_VOICE, payload: undefined });
          setVoiceResetHint(hadVoice);
          clearFieldError('sex');
          clearFieldError('voice');
          return;
        }

        case ActionEnum.SET_VOICE:
          dispatch({
            type: ActionEnum.SET_VOICE,
            payload: event.target.value as string,
          });
          clearFieldError('voice');
          return;

        case ActionEnum.SET_AGENT:
        case ActionEnum.SET_LANGUAGE:
          dispatch({ type, payload: event.target.value as string });
          clearFieldError(type === ActionEnum.SET_AGENT ? 'agent' : 'language');
          return;

        case ActionEnum.SET_TRANSCRIBER:
          dispatch({ type, payload: event.target.value as string });
          clearFieldError('transcriberId');
          return;

        default:
          // no need to handle RESET
          return;
      }
    };

  const dropUnavailableSelections = useCallback((): RemovedAgentSettings => {
    if (isWaitingForCompany) return NO_REMOVED_AGENT_SETTINGS;

    const unavailable = getUnavailableSelections(
      {
        voiceId: state.voice,
        transcriberId: state.transcriberId,
        llmProviderId: selectedProviderId,
        llmModelId: providerAllowsCustom ? null : selectedModelId,
      },
      {
        voices: voiceData,
        transcribers: transcriberData,
        llmProviders,
        llmModels: currentLlmModels,
      },
    );
    if (!hasRemovedAgentSettings(unavailable)) return unavailable;

    if (unavailable.voice) dispatch({ type: ActionEnum.SET_VOICE, payload: '' });
    if (unavailable.transcriber) dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: '' });
    if (unavailable.llmProvider) {
      setSelectedProviderId('');
      setProviderAllowsCustom(false);
      setCustomModelKey('');
    }
    if (unavailable.llmProvider || unavailable.llmModel) setSelectedModelId('');
    setStaleSelections((prev) => ({
      voice: prev.voice || unavailable.voice,
      transcriber: prev.transcriber || unavailable.transcriber,
      llmProvider: prev.llmProvider || unavailable.llmProvider,
      llmModel: prev.llmModel || unavailable.llmModel,
    }));
    return unavailable;
  }, [
    isWaitingForCompany,
    state.voice,
    state.transcriberId,
    selectedProviderId,
    providerAllowsCustom,
    selectedModelId,
    voiceData,
    transcriberData,
    llmProviders,
    currentLlmModels,
  ]);

  // A background refetch can remove a selected voice, transcriber, provider or model. Also runs on
  // selection changes, which is a no-op: the user can pick only values that are in the lists.
  useEffect(() => {
    dropUnavailableSelections();
  }, [dropUnavailableSelections]);

  type ValidatedValues = {
    voice?: string;
    transcriberId: string;
    selectedProviderId: string;
    selectedModelId: string;
    customModelKey: string;
    providerAllowsCustom: boolean;
  };

  const validateRequiredFields = (
    values: ValidatedValues = {
      voice: state.voice,
      transcriberId: state.transcriberId,
      selectedProviderId,
      selectedModelId,
      customModelKey,
      providerAllowsCustom,
    },
  ): boolean => {
    const isProfileSelected = Boolean(state.profile?.id);

    const nextErrors: Record<FieldKey, string> = {
      agent: state.agent?.trim()
        ? ''
        : (t('adminMainPage.agents.fieldValidation.enterHumanName') as string),
      sex: state.sex ? '' : (t('adminMainPage.agents.fieldValidation.selectSex') as string),
      voice:
        state.sex && !values.voice
          ? (t('adminMainPage.agents.fieldValidation.selectVoice') as string)
          : '',
      language: state.language
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectLanguage') as string),
      transcriberId: values.transcriberId
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectTranscriber') as string),
      profile: isProfileSelected
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectProfile') as string),
      llmProviderId: values.selectedProviderId
        ? ''
        : hasNoLlmProviders
          ? noLlmProvidersMessage
          : (t('adminMainPage.agents.fieldValidation.selectLlmProvider') as string),
      llmModel: !values.selectedProviderId
        ? ''
        : values.providerAllowsCustom && !values.customModelKey?.trim()
          ? (t('adminMainPage.agents.fieldValidation.enterCustomLlmModel') as string)
          : !values.providerAllowsCustom && !values.selectedModelId
            ? (t('adminMainPage.agents.fieldValidation.selectLlmModel') as string)
            : '',
    };

    setFieldErrors(nextErrors);

    const isValid = Object.values(nextErrors).every((value) => !value);
    if (!isValid) {
      window.requestAnimationFrame(() => focusFirstError(nextErrors));
    }

    return isValid;
  };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isWaitingForCompany) return;

    stopAudioPlayback();
    setShowValidation(true);

    const unavailable = dropUnavailableSelections();
    const isProviderKept = !unavailable.llmProvider;
    const isValid = validateRequiredFields({
      voice: unavailable.voice ? '' : state.voice,
      transcriberId: unavailable.transcriber ? '' : state.transcriberId,
      selectedProviderId: isProviderKept ? selectedProviderId : '',
      selectedModelId: isProviderKept && !unavailable.llmModel ? selectedModelId : '',
      customModelKey: isProviderKept ? customModelKey : '',
      providerAllowsCustom: isProviderKept && providerAllowsCustom,
    });

    if (!isValid) {
      setSaveShake(false);
      window.requestAnimationFrame(() => {
        setSaveShake(true);
      });
      window.setTimeout(() => setSaveShake(false), 650);
      return;
    }

    setValue('name', state.agent);
    setValue('voiceId', state?.voice);
    setValue('transcriberId', state?.transcriberId); // TODO: to be implemented CLARIFY WITH MAXIM
    setValue('languages', [mapLanguageToISO639(state.language)]);
    setValue('profile', state.profile);
    setValue('profileId', state.profile?.id);

    // Map LLM config to form values according to rules
    setValue('llmProviderId', selectedProviderId);
    if (providerAllowsCustom) {
      // If provider allows custom models -> send only custom key
      setValue('llmCustomModelKey', customModelKey || null);
      setValue('llmModelId', null);
    } else {
      // If provider does not allow custom models -> send model id
      setValue('llmModelId', selectedModelId || null);
      setValue('llmCustomModelKey', null);
    }

    if (agent) {
      setValue('id', agent?.id || '');
    }

    const success = await onSubmit();
    if (!success) {
      return;
    }

    dispatch({ type: ActionEnum.RESET });
    setShowValidation(false);
    setVoiceResetHint(false);
    if (!agent && onCreated) {
      await onCreated();
      return;
    }
    await onClose();
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    stopAudioPlayback();

    onClose();
    dispatch({ type: ActionEnum.RESET });
    setShowValidation(false);
    setVoiceResetHint(false);
  };

  const showVoiceRemovedHint =
    (removedSettings.voice || staleSelections.voice) && !state.voice && !voiceResetHint;
  const showTranscriberRemovedHint =
    (removedSettings.transcriber || staleSelections.transcriber) && !state.transcriberId;
  const showLlmProviderRemovedHint =
    (removedSettings.llmProvider || staleSelections.llmProvider) && !selectedProviderId;
  const showLlmModelRemovedHint =
    ((removedSettings.llmModel && selectedProviderId === agent?.llmProviderId) ||
      staleSelections.llmModel) &&
    !providerAllowsCustom &&
    !selectedModelId;

  const waitingForCompanyMessage = isWaitingForCompany
    ? (t(
        isCompanyError
          ? 'adminMainPage.agents.fieldValidation.companySettingsUnavailable'
          : 'adminMainPage.agents.fieldValidation.companySettingsLoading',
      ) as string)
    : undefined;
  const saveTooltip =
    waitingForCompanyMessage ??
    (showValidation && Object.values(fieldErrors).some(Boolean)
      ? (t('adminMainPage.agents.fieldValidation.fixErrorsBeforeSave') as string)
      : (t('common.save') as string));

  const sexOptions = Array.from(new Set(voiceData?.map((voice) => voice.gender))).map((gender) => {
    const genderLower = (gender || '').toLowerCase();
    return {
      key: gender || '',
      value: gender || '',
      label: (gender ?? '').charAt(0).toUpperCase() + (gender ?? '').slice(1) || '',
      icon:
        genderLower === 'male' ? (
          <MaleIcon className={classes.maleIcon} />
        ) : genderLower === 'female' ? (
          <FemaleIcon className={classes.femaleIcon} />
        ) : null,
    };
  });

  const filteredVoices = useMemo(
    () => voiceData?.filter((voice) => voice.gender === state.sex) || [],
    [voiceData, state.sex],
  );

  const transcriberOptions =
    transcriberData?.map((transcriber) => {
      return {
        key: transcriber.id,
        value: transcriber.id,
        label: formatTranscriberLabel(transcriber),
      };
    }) || [];

  const playVoicePreview = (previewUrl: string) => {
    if (isPlayingVoice || !previewUrl) return;

    stopAudioPlayback();

    const stopUi = () => {
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    };
    const audio = playVoicePreviewFromUserGesture(previewUrl, stopUi);
    if (!audio) return;
    audio.addEventListener('error', stopUi);
    audio.addEventListener('ended', stopUi);

    setIsPlayingVoice(true);
    setCurrentAudio(audio);
  };

  const handleVoiceIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (isPlayingVoice && currentAudio) {
      stopAudioPlayback();
      return;
    }

    if (!state.sex || !state.voice) {
      console.warn('Please select a gender and voice first');
      return;
    }

    const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
    if (!selectedVoice?.previewUrl) {
      console.warn('No preview URL available for this voice');
      return;
    }

    playVoicePreview(selectedVoice.previewUrl);
  };

  if (!user?.companyId) {
    return <div>User not found...</div>;
  }

  return (
    <>
      <TableCell className={sharedClasses.agentIdCell} sx={{ padding: '8px' }} />
      <TableCell className={sharedClasses.startCell} sx={{ padding: '8px' }} />
      <TableCell ref={setFieldAnchor('agent')} className={sharedClasses.nameCell}>
        <CustomTextField
          control={control}
          name="name"
          value={state.agent}
          onChange={handleInputChange(ActionEnum.SET_AGENT)}
          id="agent-name"
          type="text"
          placeholder={t('campaignPage.placeholders.humanName')}
          varient="outlined"
          inputProps={{ maxLength: 56 }}
          fullWidth
          required
          error={showValidation && Boolean(fieldErrors.agent)}
          helperText={showValidation ? fieldErrors.agent : ''}
          disabled={isPlayingVoice}
        />
      </TableCell>

      <TableCell ref={setFieldAnchor('sex', 'voice')} className={sharedClasses.voiceCell}>
        <CompositeFieldContainer>
          <Box sx={{ minWidth: 0 }}>
            <CustomSelectField
              control={control}
              labelId="sex-type"
              name="sexType"
              value={state.sex}
              onChange={handleInputChange(ActionEnum.SET_SEX)}
              placeholder={t('campaignPage.placeholders.sexType')}
              options={sexOptions}
              disabled={isPlayingVoice}
              className={classes.sexSelect}
              error={showValidation && Boolean(fieldErrors.sex)}
              renderValue={(selected) => {
                const selectedSex = sexOptions?.find((option) => option.key === selected);
                return (
                  <Box className={classes.genderSelectContainer}>
                    {selectedSex?.icon || selectedSex?.label || ''}
                  </Box>
                );
              }}
            />
            {showValidation && fieldErrors.sex ? (
              <FieldErrorSlot className={classes.fieldErrorText} message={fieldErrors.sex} />
            ) : null}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box className={classes.voiceContainer}>
              <Box className={classes.voiceSelectWrapper}>
                {(() => {
                  const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
                  const hasPreview = selectedVoice?.previewUrl;
                  const canPlay = state.sex && state.voice && hasPreview;
                  const isDisabled = !state.sex || !state.voice;

                  const iconClasses = [
                    classes.voiceIcon,
                    isPlayingVoice
                      ? classes.voiceIconPlaying
                      : hasPreview
                        ? classes.voiceIconWithPreview
                        : classes.voiceIconNoPreview,
                    isDisabled ? classes.voiceIconDisabled : '',
                    isPlayingVoice ? classes.voiceIconAnimated : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <Tooltip
                      title={
                        isDisabled
                          ? t('campaignPage.placeholders.selectVoiceFirst')
                          : hasPreview
                            ? t('adminMainPage.agents.voicePreviewAvailable')
                            : t('adminMainPage.agents.noVoicePreview')
                      }
                      arrow
                      placement="top">
                      <Box
                        role="button"
                        aria-label={
                          hasPreview
                            ? t('adminMainPage.agents.voicePreviewAvailable')
                            : t('adminMainPage.agents.noVoicePreview')
                        }
                        onClick={canPlay ? handleVoiceIconClick : undefined}
                        className={iconClasses}>
                        {hasPreview ? (
                          <VolumeUpIcon className={classes.volumeIcon} />
                        ) : (
                          <VolumeOffIcon className={classes.volumeIcon} />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })()}
                <VoicePicker
                  voices={filteredVoices}
                  selectedVoiceId={state.voice || ''}
                  disabled={!state.sex || isPlayingVoice}
                  error={showValidation && Boolean(fieldErrors.voice)}
                  placeholder={t('campaignPage.placeholders.voiceType')}
                  onChange={(voiceId) => {
                    dispatch({
                      type: ActionEnum.SET_VOICE,
                      payload: voiceId,
                    });
                    setVoiceResetHint(false);
                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, voice: '' }));
                    }
                  }}
                />
              </Box>
            </Box>
            {showValidation && !fieldErrors.sex && fieldErrors.voice ? (
              <FieldErrorSlot className={classes.fieldErrorText} message={fieldErrors.voice} />
            ) : null}
          </Box>
          {!state.sex && !showValidation && !removedSettings.voice ? (
            <Typography className={classes.fieldHintText} sx={{ color: 'text.secondary' }}>
              {t('adminMainPage.agents.fieldValidation.selectSexFirst')}
            </Typography>
          ) : null}
          {showVoiceRemovedHint ? (
            <Typography className={classes.fieldHintText}>
              {t('adminMainPage.agents.fieldValidation.voiceRemovedChooseReplacement')}
            </Typography>
          ) : null}
          {voiceResetHint ? (
            <Typography className={classes.fieldHintText}>
              {t('adminMainPage.agents.fieldValidation.voiceResetHint')}
            </Typography>
          ) : null}
        </CompositeFieldContainer>
      </TableCell>

      <TableCell ref={setFieldAnchor('language')} className={sharedClasses.languageCell}>
        <CustomSelectField
          control={control}
          labelId="language-type"
          name="languageType"
          value={state.language}
          onChange={handleInputChange(ActionEnum.SET_LANGUAGE)}
          placeholder={t('campaignPage.placeholders.languageType')}
          options={accentOptions}
          error={showValidation && Boolean(fieldErrors.language)}
          disabled={isPlayingVoice}
          renderValue={(selected) => {
            const selectedLang = accentOptions?.find((lang) => lang.key === selected);
            return selectedLang?.label || '';
          }}
        />
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.language : ''}
        />
      </TableCell>

      <TableCell ref={setFieldAnchor('transcriberId')} className={sharedClasses.transcriberCell}>
        <CustomSelectField
          control={control}
          labelId="transcriber-provider"
          name="transcriberId"
          value={state.transcriberId}
          onChange={handleInputChange(ActionEnum.SET_TRANSCRIBER)}
          placeholder={t('campaignPage.placeholders.transcriberType')}
          options={transcriberOptions}
          error={showValidation && Boolean(fieldErrors.transcriberId)}
          disabled={isPlayingVoice}
          renderValue={(selected) => {
            const selectedTranscriber = transcriberOptions?.find(
              (transcriber) => transcriber.key === selected,
            );
            return selectedTranscriber?.label || '';
          }}
        />
        {showTranscriberRemovedHint ? (
          <Typography className={classes.fieldHintText}>
            {t('adminMainPage.agents.fieldValidation.transcriberRemovedChooseReplacement')}
          </Typography>
        ) : null}
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.transcriberId : ''}
        />
      </TableCell>

      <TableCell
        ref={setFieldAnchor('llmProviderId', 'llmModel')}
        className={sharedClasses.llmCell}>
        <CompositeFieldContainer>
          <Tooltip
            title={hasNoLlmProviders ? noLlmProvidersMessage : ''}
            arrow
            placement="top"
            disableHoverListener={!hasNoLlmProviders}>
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              <CustomSelectField
                control={control}
                labelId="llm-provider"
                name="llmProviderId"
                value={selectedProviderId}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value as string;
                  setSelectedProviderId(value);
                  const provider = llmProviders?.find((p) => p.id === value);
                  const allows = provider?.allowsCustomModels ?? false;
                  setProviderAllowsCustom(allows);
                  // Reset incompatible fields when provider changes
                  setCustomModelKey('');
                  setSelectedModelId('');
                  setStaleSelections((prev) => ({ ...prev, llmModel: false }));
                  if (showValidation) {
                    setFieldErrors((prev) => ({ ...prev, llmProviderId: '', llmModel: '' }));
                  }
                }}
                placeholder={
                  hasNoLlmProviders
                    ? (t('adminMainPage.agents.noneAvailable') as string)
                    : (t('adminMainPage.agents.llmProvider') as string)
                }
                options={
                  llmProviders?.map((p) => ({ key: p.id, value: p.id, label: p.title })) || []
                }
                error={showValidation && Boolean(fieldErrors.llmProviderId)}
                disabled={isPlayingVoice || hasNoLlmProviders}
                renderValue={(selected) => {
                  if (hasNoLlmProviders) {
                    const emptyLabel = t('adminMainPage.agents.noneAvailable') as string;
                    return (
                      <Tooltip title={noLlmProvidersMessage} arrow placement="top">
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {emptyLabel}
                        </Box>
                      </Tooltip>
                    );
                  }
                  const selectedProvider = llmProviders?.find((p) => p.id === selected);

                  const agentProviderTitle =
                    selected && selected === agent?.llmProviderId && !removedSettings.llmProvider
                      ? agent?.llmProvider?.title
                      : undefined;
                  const label =
                    selectedProvider?.title ||
                    agentProviderTitle ||
                    (t('adminMainPage.agents.llmProvider') as string);
                  return (
                    <Tooltip title={label} arrow placement="top">
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}
                      </Box>
                    </Tooltip>
                  );
                }}
              />
            </Box>
          </Tooltip>
          {hasNoLlmProviders ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
              {noLlmProvidersMessage}
            </Typography>
          ) : null}
          {showLlmProviderRemovedHint ? (
            <Typography className={classes.fieldHintText}>
              {t('adminMainPage.agents.fieldValidation.llmProviderRemovedChooseReplacement')}
            </Typography>
          ) : null}

          {selectedProviderId &&
            (providerAllowsCustom ? (
              <>
                <CustomTextField
                  control={control}
                  name="llmCustomModelKey"
                  value={customModelKey}
                  onChange={(e: any) => {
                    setCustomModelKey(e.target.value);
                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, llmModel: '' }));
                    }
                  }}
                  id="llm-custom-model-key"
                  type="text"
                  placeholder={t('adminMainPage.agents.llmCustomModelKey')}
                  varient="outlined"
                  fullWidth
                  required
                  error={showValidation && Boolean(fieldErrors.llmModel)}
                  disabled={isPlayingVoice}
                />
                <FieldErrorSlot
                  className={classes.fieldErrorText}
                  message={showValidation ? fieldErrors.llmModel : ''}
                />
              </>
            ) : (
              <CustomSelectField
                control={control}
                labelId="llm-model"
                name="llmModelId"
                value={selectedModelId}
                onChange={(e) => {
                  setSelectedModelId((e.target as HTMLInputElement).value as string);
                  if (showValidation) {
                    setFieldErrors((prev) => ({ ...prev, llmModel: '' }));
                  }
                }}
                placeholder={t('adminMainPage.agents.llmModel')}
                options={llmModels?.map((m) => ({ key: m.id, value: m.id, label: m.title })) || []}
                error={showValidation && Boolean(fieldErrors.llmModel)}
                disabled={isPlayingVoice}
                renderValue={(selected) => {
                  const selectedModel = llmModels?.find((m) => m.id === selected);
                  const label =
                    selectedModel?.title || (t('adminMainPage.agents.llmModel') as string);
                  return (
                    <Tooltip title={label} arrow placement="top">
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}
                      </Box>
                    </Tooltip>
                  );
                }}
              />
            ))}
          {showLlmModelRemovedHint ? (
            <Typography className={classes.fieldHintText}>
              {t('adminMainPage.agents.fieldValidation.llmModelRemovedChooseReplacement')}
            </Typography>
          ) : null}
          <FieldErrorSlot
            className={classes.fieldErrorText}
            message={showValidation ? fieldErrors.llmProviderId : ''}
          />
          {!providerAllowsCustom ? (
            <FieldErrorSlot
              className={classes.fieldErrorText}
              message={showValidation ? fieldErrors.llmModel : ''}
            />
          ) : null}
        </CompositeFieldContainer>
      </TableCell>

      <TableCell ref={setFieldAnchor('profile')} className={sharedClasses.profileCell}>
        <RolePicker
          profiles={agentProfileSelectList}
          selected={
            agentProfileSelectList?.find((profile) => profile.id === state?.profile?.id) ?? null
          }
          disabled={isPlayingVoice}
          error={showValidation && Boolean(fieldErrors.profile)}
          onChange={(profile) => {
            dispatch({
              type: ActionEnum.SET_PROFILE,
              payload: profile as AgentProfileRequestDto,
            });
            if (showValidation) {
              setFieldErrors((prev) => ({ ...prev, profile: '' }));
            }
          }}
        />
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.profile : ''}
        />
      </TableCell>

      <TableCell className={sharedClasses.actionsCell}>
        <Box
          display="flex"
          flexDirection={'column'}
          justifyContent="flex-end"
          alignItems="center"
          gap={1}>
          <Tooltip title={saveTooltip} arrow placement="left">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <button
                className={classNames(classes.save_button, {
                  [classes.save_button_shake]: saveShake,
                })}
                disabled={isCreatingAgent || isWaitingForCompany}
                onClick={handleSave}
                type="button"
                aria-label={t('common.save') as string}
                aria-description={waitingForCompanyMessage}>
                <CheckIcon htmlColor="white" style={{ width: 16 }} />
              </button>
            </Box>
          </Tooltip>

          <button className={classes.cancel_button} color="secondary" onClick={handleCancel}>
            <CloseIcon htmlColor={theme.palette.text.secondary} style={{ width: 16 }} />
          </button>
        </Box>
      </TableCell>
    </>
  );
};
